// Helper exports a SINGLE function `formatTimeWindow` consumed by every
// listing card / detail view. Translation happens here: every user-facing
// string goes through i18n.t() bound to the `listing` namespace.
//
// Localization has two layers:
//   1. Names (weekday / month) and clock times are rendered via Intl bound
//      to the active `i18n.language`, so es-MX gets "viernes" / "12 de abril"
//      / "5:00 p.m." instead of English.
//   2. English keeps two hand-rolled house-style bits that Intl won't
//      reproduce — the "April 12th" ordinal suffix and the compact
//      "5:00pm" / range-abbreviated "5:00 to 8:30pm" time. These are gated
//      behind `isEnglish(locale)`; every other locale defers fully to Intl.
// The internal parsing primitives (getHM / getDateKey / getYear / weekday
// short) stay pinned to 'en-US' on purpose — they extract Latin-digit
// numbers for math and MUST stay locale-invariant.
//
// Mirrors legacy web/src/helpers/availabilityHelper.ts + dateHelper.ts,
// adapted for the new backend's `time_window: { start, end, timezone }` shape
// instead of `sale_dates` + `pickup_time`.
//
// Output strings (home-feed style, "Available " prefix stripped):
//   "Now"
//   "Now to 8:30pm"
//   "Today 5:00 to 8:30pm"
//   "Tonight 7:00 to 9:00pm"
//   "Tomorrow 5:00pm"
//   "Saturday 5:00 to 8:30pm"
//   "April 12th, 5:00 to 8:30pm"
//   "April 12th 2027, 5:00 to 8:30pm"

import i18n from 'i18next';
import type { TimeWindow } from '../types/listing';
import { zonedWallClockToInstant } from './timezone';

// i18n.t() with the `listing` namespace bound — used from non-component code
// (this is a util, not a React tree). Doesn't subscribe to lang changes, but
// TanStack Query invalidation on lang flip causes listing re-renders that
// call this helper again with the new lang. See `docs/i18n.md` "How language
// switching works".
const t = (key: string, params?: Record<string, unknown>): string =>
  i18n.t(key, { ns: 'listing', ...(params ?? {}) });

// English (any region tail — `en`, `en-US`, …) keeps the hand-rolled house
// style; every other supported locale defers to Intl. Region-insensitive so
// a future `en-GB` still routes down the English path.
const isEnglish = (locale: string): boolean =>
  locale.toLowerCase().startsWith('en');

// --- timezone-aware primitives ---

const tzParts = (
  date: Date,
  timezone: string,
  options: Intl.DateTimeFormatOptions,
): Record<string, string> => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    ...options,
  }).formatToParts(date);
  const out: Record<string, string> = {};
  for (const p of parts) out[p.type] = p.value;
  return out;
};

const getHM = (date: Date, timezone: string): { hour: number; minute: number } => {
  const p = tzParts(date, timezone, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return { hour: Number(p.hour), minute: Number(p.minute) };
};

const getDateKey = (date: Date, timezone: string): string => {
  const p = tzParts(date, timezone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return `${p.year}-${p.month}-${p.day}`;
};

const getYear = (date: Date, timezone: string): number =>
  Number(tzParts(date, timezone, { year: 'numeric' }).year);

// English house style, mirrors dateHelper.formatAvailabilityTime: "5:00pm"
const formatAvailabilityTimeEn = (hour: number, minute: number): string => {
  const minuteStr = String(minute).padStart(2, '0');
  const hour12 = ((hour + 11) % 12) + 1;
  const period = hour >= 12 ? 'pm' : 'am';
  return `${hour12}:${minuteStr}${period}`;
};

// Intl clock-time split into full text, text without the day-period token,
// and the day-period itself — so the range can drop a SHARED period from the
// start ("5:00 p.m. a 8:30 p.m." -> "5:00 a 8:30 p.m."). 24h locales have no
// day-period, so `period` is null and nothing is dropped.
const intlClockParts = (
  date: Date,
  timezone: string,
  locale: string,
): { text: string; textNoPeriod: string; period: string | null } => {
  const parts = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).formatToParts(date);
  const period = parts.find((p) => p.type === 'dayPeriod')?.value ?? null;
  return {
    text: parts.map((p) => p.value).join(''),
    textNoPeriod: parts
      .filter((p) => p.type !== 'dayPeriod')
      .map((p) => p.value)
      .join('')
      .trim(),
    period,
  };
};

// Locale-aware single clock time. English keeps the compact house style
// ("5:00pm"); every other locale defers to Intl for its own convention
// (es-MX renders "5:00 p.m."). `locale` is resolved in formatTimeWindow.
const formatClockTime = (
  date: Date,
  timezone: string,
  locale: string,
): string => {
  if (isEnglish(locale)) {
    const { hour, minute } = getHM(date, timezone);
    return formatAvailabilityTimeEn(hour, minute);
  }
  return intlClockParts(date, timezone, locale).text;
};

// Mirrors dateHelper.formatAvailabilityTimeRange: "5:00 to 8:30pm".
// The "from"/"to" join is a translation key (en "{{from}} to {{to}}",
// es-MX "{{from}} a {{to}}"). English additionally drops the shared am/pm
// from the start of the range — an Intl output would be too fragile to
// splice, so the abbreviation is English-only.
const formatAvailabilityTimeRange = (
  start: Date,
  end: Date,
  timezone: string,
  locale: string,
): string => {
  let from: string;
  let to: string;
  if (isEnglish(locale)) {
    const s = getHM(start, timezone);
    const e = getHM(end, timezone);
    from = formatAvailabilityTimeEn(s.hour, s.minute);
    to = formatAvailabilityTimeEn(e.hour, e.minute);
    const fromPeriod = from.match(/(am|pm)$/i)?.[1]?.toLowerCase();
    const toPeriod = to.match(/(am|pm)$/i)?.[1]?.toLowerCase();
    if (fromPeriod && toPeriod && fromPeriod === toPeriod) {
      from = from.replace(/\s*(am|pm)$/i, '');
    }
  } else {
    const f = intlClockParts(start, timezone, locale);
    const l = intlClockParts(end, timezone, locale);
    // Drop the shared day-period from the start, matching the English
    // abbreviation. Only when both share a period (and it exists at all).
    from = f.period && l.period && f.period === l.period ? f.textNoPeriod : f.text;
    to = l.text;
  }
  return t('timeWindow.timeRange', { from, to });
};

// Mirrors dateHelper.isEveningTime
const isEveningHour = (hour: number): boolean => hour >= 17;

// Mirrors date-fns isSameWeek (Sunday-start)
const isSameWeekSunStart = (
  target: Date,
  now: Date,
  timezone: string,
): boolean => {
  const weekdayMap: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
  };
  const nowDow =
    weekdayMap[tzParts(now, timezone, { weekday: 'short' }).weekday] ?? 0;
  const targetDow =
    weekdayMap[tzParts(target, timezone, { weekday: 'short' }).weekday] ?? 0;
  const diffDays = Math.floor(
    (target.getTime() - now.getTime()) / (24 * 60 * 60 * 1000),
  );
  // Within the current Sun–Sat week
  return diffDays >= -nowDow && diffDays <= 6 - nowDow && targetDow >= 0;
};

const ordinalSuffix = (n: number): string => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return 'th';
  switch (n % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

// English mirrors date-fns format(parsed, 'MMMM do') / 'MMMM do yyyy'
// ("April 12th" / "April 12th 2027"). Non-English locales defer fully to
// Intl so native ordering and connectors are correct ("12 de abril" /
// "12 de abril de 2027") — Intl won't emit the English ordinal suffix.
const formatMonthDay = (
  date: Date,
  timezone: string,
  withYear: boolean,
  locale: string,
): string => {
  if (!isEnglish(locale)) {
    return new Intl.DateTimeFormat(locale, {
      month: 'long',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {}),
      timeZone: timezone,
    }).format(date);
  }
  const p = tzParts(date, timezone, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const day = Number(p.day);
  const base = `${p.month} ${day}${ordinalSuffix(day)}`;
  return withYear ? `${base} ${p.year}` : base;
};

// Mirrors dateHelper.getRelativeDateMeta
const getRelativeDateMeta = (
  target: Date,
  now: Date,
  timezone: string,
  startHourForLabel: number,
  locale: string,
): { label: string; isDate: boolean } => {
  const targetKey = getDateKey(target, timezone);
  const todayKey = getDateKey(now, timezone);

  if (targetKey === todayKey) {
    return {
      label: isEveningHour(startHourForLabel) ? t('timeWindow.tonight') : t('timeWindow.today'),
      isDate: false,
    };
  }

  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  if (targetKey === getDateKey(tomorrow, timezone)) {
    return { label: t('timeWindow.tomorrow'), isDate: false };
  }

  if (getYear(target, timezone) !== getYear(now, timezone)) {
    return { label: formatMonthDay(target, timezone, true, locale), isDate: true };
  }

  if (isSameWeekSunStart(target, now, timezone)) {
    return {
      label: new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        timeZone: timezone,
      }).format(target),
      isDate: false,
    };
  }

  return { label: formatMonthDay(target, timezone, false, locale), isDate: true };
};

/**
 * Capitalize the first letter of the relative-date label so the home-feed
 * short form reads "Today 5:00 to 8:30pm" instead of "today 5:00...". For
 * relative words (today/tonight/tomorrow) the locale bundle already supplies
 * the capitalized form; for weekday + month-day formats, Intl returns
 * locale-default capitalization (e.g. Spanish `Intl` returns "lunes"
 * lowercase), so we apply the JS-side first-letter capitalize uniformly.
 * Acceptable trade-off: "Lunes" capitalized in standalone label context is
 * not jarring in Spanish.
 */
const capitalizeFirst = (s: string): string =>
  s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Render the listing's availability label in the user's UI language.
 *
 * Output shape (home-feed style — no "Available " prefix):
 *
 *   - ""                                   (no window — caller should hide the slot)
 *   - "Now to 8:30pm" / "Ahora hasta…"     (currently in window)
 *   - "Today 5:00 to 8:30pm"               (today, before or after the window)
 *   - "Friday 5:00 to 8:30pm"              (weekday, before or after the window)
 *   - "April 12th, 5:00 to 8:30pm"         (dated, before or after the window)
 *
 * Separator between when + range:
 *   - relative word / weekday → space
 *   - month-day date → comma + space
 */
/**
 * Just the clock range of a window ("6:30 to 8:30pm"), with no relative-date
 * prefix. Used by the dish card when it stacks two hand-off windows: the first
 * pill carries the day ("Tonight 6:30 to 8:30pm") and the second only needs the
 * times, since repeating "Tonight" on both reads as noise.
 *
 * Returns "" for a missing window so callers can hide the slot.
 */
export const formatTimeRange = (timeWindow: TimeWindow | null): string => {
  if (!timeWindow) return '';
  const tz = timeWindow.timezone;
  return formatAvailabilityTimeRange(
    zonedWallClockToInstant(timeWindow.start, tz),
    zonedWallClockToInstant(timeWindow.end, tz),
    tz,
    i18n.language || 'en',
  );
};

export const formatTimeWindow = (
  timeWindow: TimeWindow | null,
  now: Date = new Date(),
): string => {
  // Missing window: return empty so callers can conditionally hide the slot.
  // The legacy "Now" fallback was misleading — it conflated "no info" with
  // "currently in window."
  if (!timeWindow) return '';

  const tz = timeWindow.timezone;
  // Resolve the stored wall-clock in the LISTING's zone, not the viewer's
  // browser zone — otherwise a listing published in another zone renders
  // shifted by the offset between the two. See zonedWallClockToInstant.
  const start = zonedWallClockToInstant(timeWindow.start, tz);
  const end = zonedWallClockToInstant(timeWindow.end, tz);
  const isExpired = now.getTime() > end.getTime();

  // Active UI language, canonical (guaranteed in SUPPORTED_LANGUAGES by the
  // i18n detection chain). Drives every Intl formatter below.
  const locale = i18n.language || 'en';

  const { hour: startHour } = getHM(start, tz);
  const startMeta = getRelativeDateMeta(start, now, tz, startHour, locale);
  const timeRange = formatAvailabilityTimeRange(start, end, tz, locale);

  // Currently within the window (only when not expired).
  if (!isExpired && now.getTime() >= start.getTime()) {
    const endTime = formatClockTime(end, tz, locale);
    if (endTime) return t('timeWindow.nowTo', { end: endTime });
    return t('timeWindow.now');
  }

  // Future or past window — combine the relative-date label with the optional
  // time range. No range case: just the capitalized when. With range: pick
  // comma vs space separator based on whether the when is a date vs a
  // relative word / weekday. Expired windows render the same shape; the card
  // surfaces "unavailable" via grayscale styling.
  const when = capitalizeFirst(startMeta.label);
  if (!timeRange) return when;
  return startMeta.isDate
    ? t('timeWindow.atRangeWithComma', { when, range: timeRange })
    : t('timeWindow.atRangeWithSpace', { when, range: timeRange });
};

// --- Single pickup instant ---

/**
 * A pickup time broken into the pieces a caller needs to say WHEN it is.
 *
 * `formatTimeWindow` above describes a listing's whole availability RANGE; this
 * describes the one instant the buyer actually picked. Both speak the same
 * relative-date vocabulary (Today / Tonight / Tomorrow / Saturday / April 12th)
 * so the feed card, the picker, checkout and the order card can't name the same
 * day three different ways.
 */
export interface PickupMoment {
  /** "Today" / "Tonight" / "Tomorrow" / "Saturday" / "April 12th". */
  dayLabel: string;
  /** "5:00pm" */
  time: string;
  /** Day and time joined in the locale's own shape: "Tomorrow 5:00pm". */
  full: string;
  /** True when the pickup falls on today's date IN THE LISTING'S ZONE. */
  isToday: boolean;
  /**
   * Whole calendar days from today to the pickup date: 0 today, 1 tomorrow.
   * Negative for a window that already closed (reachable via a share link).
   */
  daysAway: number;
}

/** Midnight-UTC millis for a "YYYY-MM-DD" key, for whole-day arithmetic. */
const dayKeyToUTCMs = (key: string): number => {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

/**
 * Describe a single pickup instant relative to now.
 *
 * `pickup` is a naive wall-clock string ("YYYY-MM-DDTHH:mm:ss") in the
 * LISTING's timezone — the shape both `time_window` bounds and
 * `intendedPickupTime` use. Resolving it in that zone (rather than
 * `new Date(naive)`, which reads it as browser-local) is what keeps "5:00pm"
 * meaning 5:00pm where the food is for every viewer, and it's the same rule
 * `formatTimeWindow` follows.
 *
 * "Today" is likewise evaluated in the listing's zone: a buyer browsing from
 * another zone should be told which day they need to show up there, not which
 * day it is on their own phone.
 *
 * Returns null for a missing or unparseable pickup so callers can hide the slot.
 */
export const formatPickupMoment = (
  pickup: string | null | undefined,
  timezone: string | null | undefined,
  now: Date = new Date(),
): PickupMoment | null => {
  if (!pickup) return null;
  const at = zonedWallClockToInstant(pickup, timezone);
  if (Number.isNaN(at.getTime())) return null;

  // A legacy listing may carry no timezone; fall back to the viewer's so the
  // labels stay self-consistent rather than half-resolved.
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = i18n.language || 'en';

  const { hour } = getHM(at, tz);
  const meta = getRelativeDateMeta(at, now, tz, hour, locale);
  const dayLabel = capitalizeFirst(meta.label);
  const time = formatClockTime(at, tz, locale);
  const daysAway = Math.round(
    (dayKeyToUTCMs(getDateKey(at, tz)) - dayKeyToUTCMs(getDateKey(now, tz))) /
      86_400_000,
  );

  return {
    dayLabel,
    time,
    // Same comma-vs-space rule as the window readout: a bare date takes a
    // comma ("April 12th, 5:00pm"), a relative word or weekday takes a space.
    full: meta.isDate
      ? t('timeWindow.atRangeWithComma', { when: dayLabel, range: time })
      : t('timeWindow.atRangeWithSpace', { when: dayLabel, range: time }),
    isToday: daysAway === 0,
    daysAway,
  };
};

/**
 * `w` when it's a real window, else null. A per-method window the chef cleared
 * is stored as `{}` rather than removed, and `{}` is truthy, so a plain `??` or
 * `&&` would render it as a window with no times.
 */
export function presentWindow<W extends { start?: string | null }>(
  w: W | null | undefined,
): W | null {
  return w && w.start ? w : null;
}

/**
 * One label per hand-off window, earliest first, for compact rows that can't
 * fit DishInfoCard's pills. The envelope (`timeWindow`) spans both methods, so
 * on a listing with drop-off tonight and pickup tomorrow it reads as one long
 * block with no day change. Only the first label carries the day unless a later
 * one falls on a different date. Falls back to the envelope (method null) for
 * listings written before per-method windows existed.
 */
export function formatMethodWindows(listing: {
  fulfillmentMethod?: string[] | null;
  pickupWindow?: TimeWindow | null;
  dropoffWindow?: TimeWindow | null;
  timeWindow?: TimeWindow | null;
}): { method: 'pickup' | 'dropoff' | null; label: string }[] {
  const entries: { method: 'pickup' | 'dropoff'; window: TimeWindow }[] = [];
  const pickup = presentWindow(listing.pickupWindow);
  const dropoff = presentWindow(listing.dropoffWindow);
  if (listing.fulfillmentMethod?.includes('pickup') && pickup) entries.push({ method: 'pickup', window: pickup });
  if (listing.fulfillmentMethod?.includes('dropoff') && dropoff) entries.push({ method: 'dropoff', window: dropoff });
  if (entries.length === 0) {
    const label = formatTimeWindow(listing.timeWindow ?? null);
    return label ? [{ method: null, label }] : [];
  }
  entries.sort((a, b) => (a.window.start! < b.window.start! ? -1 : 1));
  const firstDay = entries[0].window.start!.split('T')[0];
  return entries.map((e, i) => ({
    method: e.method,
    label:
      i === 0 || e.window.start!.split('T')[0] !== firstDay
        ? formatTimeWindow(e.window)
        : formatTimeRange(e.window),
  }));
}
